interface SymbolEntry { symbol: string; nameFa: string; nameEn: string }

export function normalizeSymbolSearch(value: string): string {
  return value.normalize("NFKC").replace(/[يى]/g, "ی").replace(/ك/g, "ک")
    .replace(/[\u0640\u064b-\u065f\u0670\u200c\u200d\s]/g, "").toLocaleLowerCase("en");
}

export function matchChartSymbols(entries: readonly SymbolEntry[], query: string): SymbolEntry[] {
  const term = normalizeSymbolSearch(query);
  if (!term) return [];
  return entries.map(entry => {
    const symbol = normalizeSymbolSearch(entry.symbol);
    const rank = symbol === term ? 0 : symbol.startsWith(term) ? 1 : symbol.includes(term) ? 2
      : [entry.nameFa, entry.nameEn].some(name => normalizeSymbolSearch(name).includes(term)) ? 3 : 4;
    return { entry, rank };
  }).filter(item => item.rank < 4)
    .sort((a, b) => a.rank - b.rank || a.entry.symbol.localeCompare(b.entry.symbol, "fa"))
    .slice(0, 10).map(item => item.entry);
}

// One persisted-catalog read per mounted search. Keystrokes filter locally, never acquire market data.
export function bindChartSymbolSearch(form: HTMLFormElement): () => void {
  const input = form.querySelector<HTMLInputElement>('input[name="symbol"]')!;
  const popup = form.querySelector<HTMLElement>("[data-symbol-popup]")!;
  const list = form.querySelector<HTMLElement>('[role="listbox"]')!;
  const status = form.querySelector<HTMLElement>('[role="status"]')!;
  const doc = form.ownerDocument;
  let catalog: SymbolEntry[] | null = null;
  let matches: SymbolEntry[] = [];
  let active = -1, state: "idle" | "loading" | "ready" | "error" = "idle";
  let composing = false, dismissed = true, disposed = false;
  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const removers: (() => void)[] = [];
  const listen = (target: EventTarget, name: string, handler: (event: any) => void) => {
    target.addEventListener(name, handler);
    removers.push(() => target.removeEventListener(name, handler));
  };
  const close = () => {
    dismissed = true;
    popup.hidden = true;
    active = -1;
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
  };
  const highlight = () => {
    Array.from(list.children).forEach((item, index) => item.setAttribute("aria-selected", String(index === active)));
    if (active < 0) input.removeAttribute("aria-activedescendant");
    else {
      input.setAttribute("aria-activedescendant", `${list.id}-${active}`);
      list.children[active]?.scrollIntoView({ block: "nearest" });
    }
  };
  const select = (entry: SymbolEntry) => {
    input.value = entry.symbol;
    close();
    form.requestSubmit();
  };
  const render = () => {
    if (disposed || dismissed || composing || doc.activeElement !== input || !normalizeSymbolSearch(input.value)) {
      close();
      return;
    }
    matches = matchChartSymbols(catalog ?? [], input.value);
    active = -1;
    list.replaceChildren();
    for (const [index, entry] of matches.entries()) {
      const option = doc.createElement("li");
      option.id = `${list.id}-${index}`;
      option.setAttribute("role", "option");
      option.setAttribute("aria-selected", "false");
      option.dataset.symbolIndex = String(index);
      const symbol = doc.createElement("strong");
      symbol.textContent = entry.symbol;
      const name = doc.createElement("span");
      name.textContent = doc.documentElement.lang === "en" ? entry.nameEn || entry.nameFa : entry.nameFa || entry.nameEn;
      option.append(symbol, name);
      list.append(option);
    }
    const message = state === "loading" ? form.dataset.searchLoading : state === "error" ? form.dataset.searchError
      : matches.length ? "" : form.dataset.searchEmpty;
    status.textContent = message ?? "";
    status.hidden = !message;
    list.hidden = matches.length === 0;
    popup.hidden = false;
    input.setAttribute("aria-expanded", "true");
    highlight();
  };
  const load = async () => {
    if (state !== "idle") return;
    state = "loading";
    timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch("/api/instruments/", { credentials: "same-origin", headers: { Accept: "application/json" }, signal: controller.signal });
      if (!response.ok || response.redirected) throw new Error("SymbolCatalogUnavailable");
      const payload: unknown = await response.json();
      if (!Array.isArray(payload)) throw new Error("SymbolCatalogInvalid");
      const seen = new Set<string>();
      catalog = payload.flatMap(row => {
        if (!row || typeof row.symbol !== "string" || !row.symbol.trim() || row.symbol.length > 32 || seen.has(row.symbol)) return [];
        seen.add(row.symbol);
        return [{ symbol: row.symbol, nameFa: typeof row.nameFa === "string" ? row.nameFa : "", nameEn: typeof row.nameEn === "string" ? row.nameEn : "" }];
      });
      state = "ready";
    } catch { state = "error"; }
    finally {
      clearTimeout(timeout);
      if (!disposed) render();
    }
  };
  listen(input, "focus", () => { dismissed = false; void load(); render(); });
  listen(input, "input", () => { dismissed = false; void load(); render(); });
  listen(input, "compositionstart", () => { composing = true; close(); });
  listen(input, "compositionend", () => { composing = false; dismissed = false; void load(); render(); });
  listen(input, "keydown", (event: KeyboardEvent) => {
    if (event.isComposing || composing || event.ctrlKey || event.altKey || event.metaKey) return;
    if (event.key === "Escape") { if (!popup.hidden) event.preventDefault(); close(); }
    else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (popup.hidden) { dismissed = false; render(); }
      if (!matches.length || popup.hidden) return;
      event.preventDefault();
      active = event.key === "ArrowDown" ? (active + 1) % matches.length : (active <= 0 ? matches.length : active) - 1;
      highlight();
    } else if (event.key === "Tab") close();
  });
  listen(form, "submit", (event: SubmitEvent) => {
    if (composing) { event.preventDefault(); return; }
    if (!popup.hidden && matches.length) input.value = matches[Math.max(0, active)].symbol;
    close();
  });
  // Keep the input focused until click selection, including touch-generated focus changes.
  listen(list, "pointerdown", (event: PointerEvent) => { event.preventDefault(); });
  listen(list, "click", (event: MouseEvent) => {
    const option = (event.target as Element).closest<HTMLElement>("[data-symbol-index]");
    const entry = option && matches[Number(option.dataset.symbolIndex)];
    if (entry) select(entry);
  });
  listen(form, "focusout", (event: FocusEvent) => { if (!form.contains(event.relatedTarget as Node | null)) close(); });
  listen(doc, "pointerdown", (event: PointerEvent) => { if (!form.contains(event.target as Node)) close(); });
  return () => { disposed = true; controller.abort(); clearTimeout(timeout); removers.forEach(remove => remove()); close(); };
}

const searches = new Map<HTMLFormElement, () => void>();
export function initChartSymbolSearches(): void {
  for (const [form, dispose] of searches) if (!form.isConnected) { dispose(); searches.delete(form); }
  document.querySelectorAll<HTMLFormElement>("[data-chart-symbol-search]").forEach(form => {
    if (!searches.has(form)) searches.set(form, bindChartSymbolSearch(form));
  });
}
